import {
  addDoc,
  collection,
  getCountFromServer,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const CACHE_KEY = 'adminUsersData_v1';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minuti

export function loadCachedUsersData() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.cachedAt) return null;
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    console.log('[Admin] Cache hit');
    return parsed;
  } catch {
    return null;
  }
}

export function clearCachedUsersData() {
  localStorage.removeItem(CACHE_KEY);
}

export async function fetchUsersData() {
  console.log('[Admin] Cache miss, fetching...');
  const now = new Date();
  const monthStart = Timestamp.fromDate(
    new Date(now.getFullYear(), now.getMonth(), 1)
  );

  // 1. Tutti gli utenti (1 round-trip).
  const usersSnap = await getDocs(collection(db, 'users'));

  // 2. Olli logs del mese in un'unica query, poi raggruppati client-side.
  const olliQ = query(
    collection(db, 'olliLogs'),
    where('timestamp', '>=', monthStart)
  );
  const olliSnap = await getDocs(olliQ);
  const olliByUser = new Map();
  olliSnap.forEach((d) => {
    const data = d.data();
    const uid = data.userId;
    if (!uid) return;
    const cur = olliByUser.get(uid) || { count: 0, costUSD: 0 };
    cur.count += 1;
    cur.costUSD += data.estimatedCostUSD || 0;
    olliByUser.set(uid, cur);
  });

  // 3. Per ogni utente, count delle subcollections in parallelo.
  const userStats = await Promise.all(
    usersSnap.docs.map(async (userDoc) => {
      const uid = userDoc.id;
      const userData = userDoc.data() || {};

      const subcounts = await Promise.all([
        getCountFromServer(collection(db, 'users', uid, 'invoices')),
        getCountFromServer(collection(db, 'users', uid, 'clients')),
        getCountFromServer(collection(db, 'users', uid, 'timesheets')),
        getCountFromServer(collection(db, 'users', uid, 'f24')),
        getCountFromServer(
          collection(db, 'users', uid, 'pensionContributions')
        ),
        getCountFromServer(
          query(
            collection(db, 'users', uid, 'emailLog'),
            where('createdAt', '>=', monthStart)
          )
        ).catch(() => null),
      ]);
      const [invSnap, cliSnap, tsSnap, f24Snap, pensSnap, emailMonthSnap] =
        subcounts;

      const docCounts = {
        invoices: invSnap.data().count,
        clients: cliSnap.data().count,
        timesheets: tsSnap.data().count,
        f24: f24Snap.data().count,
        pensionContributions: pensSnap.data().count,
      };
      const documentsTotal = Object.values(docCounts).reduce(
        (a, b) => a + b,
        0
      );
      const emailMonth = emailMonthSnap ? emailMonthSnap.data().count : 0;
      const olli = olliByUser.get(uid) || { count: 0, costUSD: 0 };

      return {
        uid,
        email: userData.email || null,
        displayName: userData.displayName || null,
        role: userData.role || 'user',
        createdAt: userData.createdAt?.toMillis
          ? userData.createdAt.toMillis()
          : null,
        lastLoginAt: userData.lastLoginAt?.toMillis
          ? userData.lastLoginAt.toMillis()
          : null,
        documentsTotal,
        docCounts,
        olliRequestsMonth: olli.count,
        olliCostUSDMonth: olli.costUSD,
        emailMonth,
        storageEstimateKB: documentsTotal * 2,
      };
    })
  );

  const result = { data: userStats, cachedAt: Date.now() };
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(result));
  } catch (err) {
    console.warn('[Admin] Cache save failed:', err?.message || err);
  }
  return result;
}

export async function logAdminAccess(adminEmail) {
  try {
    await addDoc(collection(db, 'adminAccessLogs'), {
      adminEmail: adminEmail || null,
      accessedAt: serverTimestamp(),
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent || null : null,
    });
  } catch (err) {
    console.warn('[Admin] access log failed:', err?.message || err);
  }
}
